import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BusinessComponent, BusinessComponentOptions, ColumnPanel, ContentProvider, PageBreak, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { ChartType, ColumnChartName } from '@ddt-web/chart';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { ComponentData, TabData, TabUtil } from '@ddt-web/data-service';
import { EsgRatingsScale } from '@ddt-web/esg';
import { EsgTranslationUtil } from '@ddt-web/generic-utils';
import { DEFAULT_DISCLOSURE_TEXT_CLASSES } from '@ddt-web/pdf-utils';

import { ChartTableReportComponentProperties } from '../chart-table/chart-table.report.component.interface';
import { DisclosureComponent } from '../disclaimers/disclosure-component';
import { HeadingComponent, HeadingLevel } from '../heading/heading-component';
import { HighchartsSVGComponent } from '../highchart/highchart-svg-component';
import { CHART_QUALITY_SCORE_CONFIGS } from './esg-charts.config';
import { ESGMetricsHeadings } from './esg-metrics-headings.model';

export class ESGQualityScoreQuad extends BusinessComponent {
	@InjectConfig(SharedConfigKey.EsgRatingsRangeScale) private readonly esgRatingsScaleMap: EsgRatingsScale = {};

	private chartLabelFormatter = (esgTranslationMap) => function (): string {
		const esgRatingsScaleMap = esgTranslationMap;
		let formattedLabel;
		if (this.key === 'ESG Score') {
			const translatedLabel = EsgTranslationUtil.getEsgLetterRating(esgRatingsScaleMap, this.point.y);
			formattedLabel = `<span style="font-size: 9px">${translatedLabel}</span>`;
		} else {
			formattedLabel = this.point.y.toFixed(2);
		}
		return formattedLabel;
	};

	public constructor(
		public name: string,
		public dataComponentId: string,
		public contentService: ContentService,
		public headlines: ESGMetricsHeadings,
		public shouldStartFromNewPage: boolean = false,
		public visible: boolean = true
	) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
	}

	public isComponentDataVisible(componentData: ComponentData): boolean {
		return componentData && componentData.success && componentData.tabs.some(TabUtil.isTabValid);
	}

	public buildComponent(componentData: ComponentData, contentProvider: ContentProvider, applicationState?: any): Array<AbstractRenderer<SVGGElement>> {
		const { headingDcr, heading, headlineDcr, headline, showDisclaimer } = this.headlines;

		const panels: AbstractRenderer<SVGGElement>[] = [];
		if (this.isComponentDataVisible(componentData)) {
			panels.push(
				...this.shouldStartFromNewPage ? [new PageBreak()] : [],
				...this.getInitialSectionPanels(headingDcr, heading, headlineDcr, headline),
				...this.getQualityScoreCharts(componentData, CHART_QUALITY_SCORE_CONFIGS, 10)
			);

			if (showDisclaimer) {
				panels.push(new DisclosureComponent('ESG_GENERIC_DISCLOSURE_PDF', 'ESG_GENERIC_DISCLOSURE_PDF', {}, {}, DEFAULT_DISCLOSURE_TEXT_CLASSES));
			}
		}

		return panels;
	}

	private getQualityScoreCharts(componentData: ComponentData, configs: ChartTableReportComponentProperties[], yAxisMax: number): Array<AbstractRenderer<SVGGElement>> {
		const validTabs = configs
			.filter(config => {
				const tab = componentData.getTabById(config.tabId);
				return tab && TabUtil.isTabValid(tab);
			});
		const chartTabPairs: [ChartTableReportComponentProperties, ChartTableReportComponentProperties][] = validTabs.reduce((result, value, index, array) => {
			if (index % 2 === 0) {
				result.push(array.slice(index, index + 2));
			}
			return result;
		}, []);


		const chartsPanel: AbstractRenderer<SVGGElement>[] = [];
		chartsPanel.push(...chartTabPairs.map((configPair, index, chartsPanel) => {
			const showLegendOnLeft = chartsPanel.length - 1 === index;
			return this.getESGChartPair(componentData, configPair, yAxisMax, showLegendOnLeft);
		}));

		return chartsPanel;
	}

	private getInitialSectionPanels(
		headingDcr: string,
		heading: string,
		headlineDcr: string,
		headline: string
	): AbstractRenderer<SVGGElement>[] {
		const panels: AbstractRenderer<SVGGElement>[] = [];
		panels.push(new HeadingComponent(headingDcr, heading, HeadingLevel.H2));
		panels.push(new StaticParagraphComponent(headlineDcr, headline, {}, { marginBottom: 10 }));
		return panels;
	}

	private getESGChartPair(
		componentData: ComponentData,
		configPair: [ChartTableReportComponentProperties, ChartTableReportComponentProperties],
		yAxisMax: number,
		showLegendOnLeft: boolean
	): RowPanel {
		const tabs = configPair.map((config, index) => componentData.getTabById(configPair[index].tabId));

		const esgChartPair: Array<ColumnPanel> = tabs.map((tabData: TabData, index: number) => {
			const yAxisLabel = configPair[index].description;
			const showLegend = showLegendOnLeft && index === 0;
			const portfolioCount = componentData.portfolioData.length;

			return new ColumnPanel(
				'ESG Quality Score Chart',
				[
					new HeadingComponent(configPair[index].chartHeaderDcr, configPair[index].chartHeader, HeadingLevel.H3, {}),
					new HighchartsSVGComponent(
						ColumnChartName.GroupedColumnChart,
						{ height: this.getChartHeight(portfolioCount, showLegend) },
						tabData,
						ChartType.Column,
						this.getChartConfig(yAxisLabel, yAxisMax, showLegend, portfolioCount),
						-10
					)
				],
				{
					proportion: 50
				},
				Alignment.Horizontal.Left
			);
		});

		return new RowPanel(
			'ESG Quality Score Chart Pair',
			esgChartPair,
			{ proportion: 100, marginBottom: 10 },
			Alignment.Horizontal.Left
		);
	}

	private getChartConfig(yAxisText: string, yAxisMax: number, showLegend: boolean, portfolioCount: number): Object {
		const esgTranslationMap = this.esgRatingsScaleMap;
		const config = {
			yAxis: {
				tickAmount: 5,
				min: 0,
				title: {
					text: yAxisText
				},
				labels: {
					formatter: function (): string {
						return this.value;
					}
				}
			},
			xAxis: {
				title: {
					text: null
				},
				labels: {
					enabled: false
				},
				tickAmount: 0,
				tickLength: 0
			},
			chart: {
				height: showLegend ? 110 + this.getChartMarginBottom(portfolioCount, showLegend) : 120,
				marginBottom: this.getChartMarginBottom(portfolioCount, showLegend),
				marginTop: 20,
				marginLeft: 35
			},
			plotOptions: {
				column: {
					dataLabels: {
						enabled: true,
						crop: false,
						overflow: 'none',
						style: {
							fontSize: '9px'
						},
						formatter: this.chartLabelFormatter(esgTranslationMap)
					}
				}
			}
		};
		if (yAxisMax) {
			config.yAxis['max'] = yAxisMax;
		}

		if (showLegend) {
			config['legend'] = {
				align: 'left',
				verticalAlign: 'bottom',
				layout: 'vertical',
				padding: 0,
				itemMarginTop: 5,
				itemWidth: 250,
				itemStyle: {
					textOverflow: 'none'
				}
			};
		} else {
			config['legend'] = {
				enabled: false
			};
		}

		return config;
	}

	private getChartMarginBottom(portfolioCount: number, showLegend: boolean): number {
		if (showLegend) {
			switch (portfolioCount) {
				case 1:
					return 50;
				case 2:
					return 65;
				case 3:
					return 75;
			}
		} else {
			return 10;
		}
	}

	private getChartHeight(portfolioCount: number, showLegend: boolean): number {
		if (showLegend) {
			switch (portfolioCount) {
				case 1:
					return 150;
				case 2:
					return 165;
				case 3:
					return 180;
			}
		} else {
			return 120;
		}
	}
}
